<template>
  <div
    :class="{'is-dark': dark}"
    :style="[{height: `${height}px`}]"
    class="shortcuts-container"
  >
    <CustomButton
      v-for="shortcut in customShortcuts"
      :key="shortcut.key"
      :dark="dark"
      :color="color"
      :selected="selectedShortcut === shortcut.key"
      with-border
      class="shortcut-button"
      @click="select(shortcut)"
    >
      <span class="lm-fs-12 flex-1">
        {{ shortcut.label }}
      </span>
    </CustomButton>
  </div>
</template>

<script>
  import {
    endOfDay, endOfMonth, endOfQuarter, endOfWeek, endOfYear, startOfDay,
    startOfMonth, startOfQuarter, startOfWeek, startOfYear, subDays, subMonths,
    subWeeks, subYears
  } from 'date-fns'
  import CustomButton from '@/components/datetime/_subs/CustomButton'
  import { getDateFnsLocale, getWeekStartsOn, parseDateValue } from '@/components/datetime/utils'

  const SHORTCUT_TYPES = ['day', 'date', '-day', 'isoWeek', 'quarter', '-isoWeek', 'month', '-month', 'year', '-year', 'week', '-week']

  /**
   * Component used to show a list of the shortcuts currently available
   * and select one of them.
   * @module component - RangeShortcuts
   * @param {Array} customShortcuts
   */
  export default {
    name: 'RangeShortcuts',
    components: { CustomButton },
    props: {
      value: { type: String, required: false, default: null },
      color: { type: String, default: null },
      dark: { type: Boolean, default: false },
      dateTime: { type: Object, default: null },
      locale: { type: String, default: null },
      firstDayOfWeek: { type: Number, default: null },
      customShortcuts: {
        type: Array,
        default: () => ([]),
        validator: val => val.every(shortcut => {
          const isValueInteger = Number.isInteger(shortcut.value)
          const isFunction = typeof shortcut.value === 'function'
          return shortcut.key && shortcut.label && (isValueInteger || isFunction ? true : SHORTCUT_TYPES.includes(shortcut.value))
        })
      },
      height: { type: Number, required: true }
    },
    data () {
      return {
        computedTypes: {},
        selectedShortcut: null
      }
    },
    watch: {
      customShortcuts () {
        this.init()
      }
    },
    mounted () {
      this.init()
    },
    methods: {
      init () {
        this.noticeDeprecation()

        /**
         * Find the pre-selected shortcut
         */
        if (this.value) {
          const selectedShortcut = this.customShortcuts.find(shortcut => shortcut.key === this.value)
          if (selectedShortcut) this.select(selectedShortcut)
        }
      },
      /**
       * Notify the developer that he's using a deprecated API for the shortcut.
       * @function noticeDeprecation
       */
      noticeDeprecation () {
        const useDeprecatedAPI = this.customShortcuts.find(shortcut => typeof shortcut.isSelected !== 'undefined' || typeof shortcut.key === 'undefined')
        if (useDeprecatedAPI) console.warn('[vue-ctk-date-time-picker]: You\'re using a deprecated API. Check the changelog (https://github.com/chronotruck/vue-ctk-date-time-picker/releases) for migration guide.')
      },
      /**
       * Returns the shortcut values according to the key
       * @function getShortcutByKey
       * @param {string} shortcutKey
       * @returns {Object}
       */
      getShortcutByKey (shortcutKey) {
        const shortcut = this.customShortcuts.find(sc => sc.key === shortcutKey)
        if (!shortcut) return false
        const { value } = shortcut

        /**
         * Case where the value is a specific number of days.
         */
        if (typeof value === 'number') {
          return {
            start: subDays(new Date(), value),
            end: new Date(),
            value
          }
        }

        /**
         * Case where the value is a function that is in charge of
         * handling the start & end values
         */
        if (typeof value === 'function') {
          const { start, end } = value()

          if (!start || !end) throw new Error('Missing "start" or "end" values.')
          if (!parseDateValue(start, null, this.locale) || !parseDateValue(end, null, this.locale)) throw new Error('The "start" or "end" values are not valid dates.')

          return {
            start,
            end
          }
        }

        const now = new Date()
        const weekOptions = {
          locale: getDateFnsLocale(this.locale),
          weekStartsOn: getWeekStartsOn(this.locale, this.firstDayOfWeek)
        }
        switch (value) {
        case 'year':
          return {
            start: startOfYear(now),
            end: endOfYear(now),
            value
          }
        case 'month':
          return { start: startOfMonth(now), end: endOfMonth(now), value }
        case 'quarter':
          return { start: startOfQuarter(now), end: endOfQuarter(now), value }
        case 'week':
          return { start: startOfWeek(now, weekOptions), end: endOfWeek(now, weekOptions), value }
        case 'isoWeek':
          return { start: startOfWeek(now, { weekStartsOn: 1 }), end: endOfWeek(now, { weekStartsOn: 1 }), value }
        case 'day': case 'date':
          return { start: startOfDay(now), end: endOfDay(now), value }
        case '-month':
          {
            const date = subMonths(now, 1)
            return {
              start: startOfMonth(date),
              end: endOfMonth(date),
              value
            }
          }
        case '-year':
          {
            const date = subYears(now, 1)
            return { start: startOfYear(date), end: endOfYear(date), value }
          }
        case '-week':
          {
            const date = subWeeks(now, 1)
            return { start: startOfWeek(date, weekOptions), end: endOfWeek(date, weekOptions), value }
          }
        case '-isoWeek':
          {
            const date = subWeeks(now, 1)
            return { start: startOfWeek(date, { weekStartsOn: 1 }), end: endOfWeek(date, { weekStartsOn: 1 }), value }
          }
        case '-day':
          {
            const date = subDays(now, 1)
            return { start: startOfDay(date), end: endOfDay(date), value }
          }
        }
      },
      select (shortcut) {
        this.selectedShortcut = shortcut.key
        const { start, end, value } = this.getShortcutByKey(this.selectedShortcut)
        this.$emit('change-range', { start, end, value })

        /**
         * Calls a callback function (if defined) on shortcut click
         */
        if (shortcut.callback) {
          if (typeof shortcut.callback !== 'function') throw new Error('The callback must be a function.')
          shortcut.callback({
            shortcut,
            start,
            end
          })
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  .shortcuts-container {
    width: 140px;
    max-width: 140px;
    min-width: 140px;
    padding: 10px 5px;
    border-right: 1px solid #EAEAEA;
    overflow: auto;
    button.shortcut-button {
      margin-bottom: 10px;
      width: 100%;
    }
    &.is-dark {
      border-color: lighten(#424242, 20%);
    }
  }

  @media screen and (max-width: 415px) {
    .shortcuts-container:not(.inline) {
      width: 100%;
      max-width: 100%;
      min-width: 100%;
      max-width: 100vw;
      min-width: 100vw;
      border-right: 0;
      border-bottom: 1px solid #EAEAEA;
      height: 52px !important;
      flex-direction: row;
      display: flex;
      white-space: nowrap;

      .shortcut-button {
        margin-bottom: 0;
      }

      .shortcut-button:not(:last-child) {
        margin-right: 10px;
      }
    }

    .shortcuts-container.is-dark {
      border-color: lighten(#424242, 20%);
    }
  }
</style>
